
import { defineComponent, ref, toRefs, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'

import type { Maybe, Status, ActivityGroupId } from '@/types'
import { WidgetsIcon } from '@icons'
import {
  useDebouncedRef,
  useRouter,
  useActivityGroupService,
  useActivityGroupCRUD,
  useDefaultTags,
} from '@/use'
import { getCurrentInstance, indexBy } from '@/util'

import { SidebarEntity } from './Sidebar.types'
import BaseSidebarSection from './BaseSidebarSection.vue'
import SidebarList from './SidebarList.vue'
import SidebarListItem from './SidebarListItem.vue'
import SidebarTime from './SidebarTime.vue'

const fields = {
  id: true,
  name: true,
  updatedAt: true,
} as const

export default defineComponent({
  name: 'ActivityGroupSidebarSection',
  components: {
    BaseSidebarSection,
    WidgetsIcon,
    SidebarList,
    SidebarListItem,
    SidebarTime,
  },
  props: {
    expanded: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { open } = toRefs(props)
    const router = useRouter()
    const { service } = useActivityGroupService()
    const {
      create: createActivityGroup,
      edit: editActivityGroup,
      del: deleteActivityGroup,
      copy: copyActivityGroup,
    } = useActivityGroupCRUD()
    const query = ref('')

    const { tags } = useDefaultTags('activity-group-search-tags')

    const debouncedQuery = useDebouncedRef(query)
    const status = ref<Maybe<Status>>(undefined)
    const limit = 25

    const key = ['activity-groups', debouncedQuery, status, tags, fields, limit]

    const { isLoading, isRefetching, data, refetch, fetchNextPage } =
      useInfiniteQuery({
        queryKey: key,
        queryFn: ({ pageParam }) =>
          service.search(
            { query: query.value, status: status.value, tags: tags.value },
            fields,
            {
              limit,
              page: pageParam,
            }
          ),
        getNextPageParam: (page) => {
          return page.page === page.pageCount ? undefined : page.page + 1
        },
        enabled: open,
        initialPageParam: 1,
      })
    const activityGroups = computed(() =>
      (data.value?.pages ?? []).map((page) => page.data).flat()
    )
    const activityGroupsById = computed(() =>
      indexBy(activityGroups.value, 'id')
    )
    const loading = computed(() => isLoading.value || isRefetching.value)

    const draggable = computed(() => instance.proxy.$route.name === 'room')

    function toggle() {
      emit('toggle', SidebarEntity.ACTIVITY_GROUP)
    }

    async function create() {
      const res = await createActivityGroup()
      if (res === null) {
        return
      }
      await refetch()
      router.push({ name: 'group', params: { id: res.id.toString() } })
    }

    async function edit(id: ActivityGroupId) {
      const res = await editActivityGroup(id)
      if (res === null) {
        return
      }
      const activityGroup = activityGroupsById.value.get(id)
      if (activityGroup === undefined) {
        return
      }
      activityGroup.name = res.name
    }

    async function copy(id: ActivityGroupId, name: string) {
      const result = await copyActivityGroup(id, name)
      if (result === undefined) return
      router.push({ name: 'group', params: { id: result.toString() } })
      await refetch()
    }

    async function del(id: ActivityGroupId) {
      const res = await deleteActivityGroup(id)
      if (res === null) {
        return
      }
      await refetch()
    }

    return {
      activityGroups,
      loading,
      fetchNextPage,
      toggle,
      create,
      edit,
      copy,
      del,
      query,
      tags,
      status,
      draggable,
      SidebarEntity,
    }
  },
})
